/**
 * Contexto cliente para /api/capture-checkout-email → Slack "Email capturado".
 * Misma riqueza que session_started / checkout_opened (device, geo, traffic).
 */

import { detectTrafficSource } from "@/lib/detectTrafficSource";
import { getFirstVisitAttribution } from "@/lib/firstVisitAttribution";
import { getHeroCtaSlackExtra } from "@/lib/heroCtaSlackContext";
import { getSlackFunnelDiagnosticsExtraPostHog } from "@/lib/posthogAnalytics";
import { getServerTemplateConfig } from "@/lib/serverTemplateDetection";

function detectDevice(): "mobile" | "tablet" | "desktop" | "unknown" {
  if (typeof navigator === "undefined") return "unknown";
  const ua = (navigator.userAgent || "").toLowerCase();
  if (/ipad|tablet/.test(ua)) return "tablet";
  if (/mobile|iphone|android|opera mini|iemobile/.test(ua)) return "mobile";
  if (/windows|macintosh|linux|x11/.test(ua)) return "desktop";
  return "unknown";
}

function detectLanguage(): string | undefined {
  if (typeof document === "undefined") return undefined;
  const html = document.documentElement.lang;
  if (html) return html.slice(0, 16);
  if (typeof navigator !== "undefined" && navigator.language) {
    return navigator.language.slice(0, 16);
  }
  return undefined;
}

export interface CheckoutEmailCaptureContext {
  device: "mobile" | "tablet" | "desktop" | "unknown";
  language?: string;
  template?: string;
  hero_cta_type?: string;
  path?: string;
  referrer?: string;
  landing_url?: string;
  metadata: Record<string, string | number | boolean>;
}

export function buildCheckoutEmailCaptureContext(): CheckoutEmailCaptureContext {
  if (typeof window === "undefined") {
    return { device: "unknown", metadata: {} };
  }

  const tc = getServerTemplateConfig();
  const traffic = detectTrafficSource();
  const attribution = getFirstVisitAttribution();
  const hero = getHeroCtaSlackExtra();
  const diagnostics = getSlackFunnelDiagnosticsExtraPostHog({ replayNearNow: true });
  const path = window.location.pathname;
  const referrerNow =
    typeof document !== "undefined" && document.referrer
      ? document.referrer.slice(0, 300)
      : undefined;
  const screen =
    typeof window.screen !== "undefined"
      ? `${window.screen.width}x${window.screen.height}`
      : undefined;
  const ua =
    typeof navigator !== "undefined" ? navigator.userAgent.slice(0, 300) : undefined;

  const metadata: Record<string, string | number | boolean> = {
    ...diagnostics,
    traffic_source: traffic.source,
    traffic_medium: traffic.medium,
    traffic_channel: traffic.channel,
    in_app_browser: traffic.inAppBrowser || false,
    referrer_now: referrerNow ?? "",
    screen: screen ?? "",
    user_agent: ua ?? "",
    city: tc?.city?.trim() ?? "",
    province: tc?.province?.trim() ?? "",
    us_state: tc?.usState?.trim() ?? "",
    neighborhood: tc?.neighborhood?.trim() ?? "",
    neighborhood_city: tc?.neighborhoodCity?.trim() ?? "",
    geo_source: tc?.geoSource?.trim() ?? "",
    template_source: tc?.templateSource?.trim() ?? "",
    timezone: tc?.timezone?.trim() ?? "",
    postal_code: tc?.postalCode?.trim() ?? "",
    hero_cta_label: hero.hero_cta_label ?? "",
    hero_cta_url: hero.hero_cta_url ?? "",
    hero_cta_emoji: hero.hero_cta_emoji ?? "",
    geo_summary: hero.geo_summary ?? "",
    page_url: window.location.href.slice(0, 500),
  };

  for (const [k, v] of Object.entries(metadata)) {
    if (v === "" || v === false) delete metadata[k];
  }

  return {
    device: detectDevice(),
    language: tc?.language ?? detectLanguage(),
    template: tc?.templateName ?? hero.worker_template,
    hero_cta_type: tc?.ctaType ?? hero.hero_cta_type,
    path,
    referrer: attribution.referrer || hero.referrer,
    landing_url: attribution.landingUrl || hero.landingUrl,
    metadata,
  };
}